import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { createFileRoute } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { logBackupDrill, listBackupDrills } from "@/lib/phase2.functions";

export const Route = createFileRoute("/_authenticated/admin/backup-drills")({
  head: () => ({ meta: [{ title: "Backup drill log — Admin" }] }),
  component: BackupDrillPage,
});

function BackupDrillPage() {
  const qc = useQueryClient();
  const [restoreTarget, setRestoreTarget] = useState("staging");
  const [outcome, setOutcome] = useState<"success" | "partial" | "failed">("success");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");

  const { data } = useQuery({
    queryKey: ["backup-drills"],
    queryFn: () => listBackupDrills(),
  });

  const mut = useMutation({
    mutationFn: () => logBackupDrill({
      data: {
        restoreTarget,
        outcome,
        durationSeconds: duration ? Number(duration) : null,
        notes: notes || null,
        snapshotTakenAt: null,
      },
    }),
    onSuccess: () => {
      toast.success("Drill recorded");
      setNotes(""); setDuration("");
      qc.invalidateQueries({ queryKey: ["backup-drills"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  return (
    <main className="mx-auto max-w-4xl px-6 py-10">
      <BackToDashboard className="mb-4" />
      <h1 className="font-display text-3xl">Backup restore drills</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Quarterly restore drills prove backups actually work. Log each rehearsal here.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
        className="mt-6 space-y-3 border border-border bg-card p-4"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="text-xs">
            <span className="mb-1 block tracking-wide-luxury uppercase text-muted-foreground">Restore target</span>
            <input
              value={restoreTarget}
              onChange={(e) => setRestoreTarget(e.target.value)}
              className="w-full border border-border bg-background px-3 py-2"
              maxLength={200}
              required
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block tracking-wide-luxury uppercase text-muted-foreground">Outcome</span>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as typeof outcome)}
              className="w-full border border-border bg-background px-3 py-2"
            >
              <option value="success">Success</option>
              <option value="partial">Partial</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block tracking-wide-luxury uppercase text-muted-foreground">Duration (s)</span>
            <input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              type="number" min={0} max={86400}
              className="w-full border border-border bg-background px-3 py-2"
            />
          </label>
        </div>
        <label className="block text-xs">
          <span className="mb-1 block tracking-wide-luxury uppercase text-muted-foreground">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3} maxLength={2000}
            className="w-full border border-border bg-background px-3 py-2"
            placeholder="What was restored, any anomalies, action items..."
          />
        </label>
        <button
          type="submit"
          disabled={mut.isPending}
          className="border border-primary bg-primary px-6 py-2 text-xs uppercase tracking-wide-luxury text-primary-foreground disabled:opacity-50"
        >
          {mut.isPending ? "Saving…" : "Log drill"}
        </button>
      </form>

      <h2 className="mt-8 font-display text-xl">Recent drills</h2>
      <div className="mt-3 divide-y divide-border border border-border">
        {(data?.drills ?? []).map((d: {
          id: string; performed_at: string; restore_target: string;
          outcome: string; duration_seconds: number | null; notes: string | null;
        }) => (
          <div key={d.id} className="grid grid-cols-1 gap-1 p-3 text-xs sm:grid-cols-6">
            <div className="text-muted-foreground">{new Date(d.performed_at).toLocaleString()}</div>
            <div className="font-medium">{d.restore_target}</div>
            <div>
              <span className={`inline-block px-2 py-0.5 tracking-wide-luxury uppercase text-[10px] ${
                d.outcome === "success" ? "bg-emerald-100 text-emerald-800"
                : d.outcome === "partial" ? "bg-amber-100 text-amber-800"
                : "bg-red-100 text-red-800"
              }`}>{d.outcome}</span>
            </div>
            <div className="text-muted-foreground">{d.duration_seconds ? `${d.duration_seconds}s` : "—"}</div>
            <div className="sm:col-span-2 text-muted-foreground">{d.notes ?? ""}</div>
          </div>
        ))}
        {(!data || data.drills.length === 0) ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No drills logged yet.</div>
        ) : null}
      </div>
    </main>
  );
}
